/**
 * Monitoring Persona Configuration
 *
 * Main persona containing all measurement and analysis tools organized into a single mode:
 * - Selection
 * - Point measurement
 * - Distance measurement (Line)
 * - Area measurement (Polygon)
 * - Volume measurement (Box)
 * - Point cluster analysis
 * - Plane detection
 * - Screenshot
 */

import { IPersonaConfig, ToolbarElementType } from '../../types';
import { ViewModes } from '../../helpers';
import { SelectionPersonaTool, PointMeasurePersonaTool, DistanceMeasurePersonaTool, AreaMeasurePersonaTool, BoxPersonaTool, ClusterPersonaTool, SurfaceFitPersonaTool, ScreenshotPersonaTool } from '../../tools';

export const MonitoringPersonaConfig: IPersonaConfig = {
  name: 'monitoring',
  priority: 100,
  exclusive: false,

  defaultViewConfig: {
    views: [ViewModes.PERSPECTIVE_3D_RGB, ViewModes.ORTHOGRAPHIC_2D_TOP, ViewModes.XRAY_2D],
    defaultViewId: 'perspective_3d_rgb',
  },

  modes: [
    {
      id: 'default',
      name: 'monitoring',
      toolbarElements: [
        // Selection tool
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'selection',
            factory: (viewer, persona) => new SelectionPersonaTool(viewer, persona),
            defaultEnabled: true,
            exclusive: true,
            icon: 'select',
            name: 'select',
          },
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Point measurement tool
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'point_measure',
            factory: (viewer, persona) => new PointMeasurePersonaTool(viewer, persona),
            exclusive: true,
            icon: 'location',
            name: 'point',
          },
        },
        // Distance measurement tool
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'distance_measure',
            factory: (viewer, persona) => new DistanceMeasurePersonaTool(viewer, persona),
            exclusive: true,
            icon: 'vector-line',
            name: 'line',
          },
        },
        // Area measurement tool
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'area_measure',
            factory: (viewer, persona) => new AreaMeasurePersonaTool(viewer, persona),
            exclusive: true,
            icon: 'vector-polygon',
            name: 'polygon',
          },
        },
        // Box tool
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'box',
            factory: (viewer, persona) => new BoxPersonaTool(viewer, persona),
            exclusive: true,
            icon: 'cube',
            name: 'box',
          },
        },

        // Surface tool — fits a plane and creates a SURFACE group (replaces the
        // former plane annotation tool). Keeps id/icon 'plane' so the buildClipState
        // activePlane lookup and existing keybindings keep working.
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'plane',
            factory: (viewer, persona) => new SurfaceFitPersonaTool(viewer, persona),
            exclusive: true,
            icon: 'plane',
            name: 'surface',
          },
        },
        // Cluster tool
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'cluster',
            factory: (viewer, persona) => new ClusterPersonaTool(viewer, persona),
            exclusive: true,
            icon: 'magic-wand',
            name: 'cluster',
          },
        },

        // Spacer before undo/redo
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        // Undo action
        {
          type: ToolbarElementType.ACTION,
          id: 'undo',
          icon: 'undo',
          tooltip: 'undo',
        },
        // Redo action
        {
          type: ToolbarElementType.ACTION,
          id: 'redo',
          icon: 'redo',
          tooltip: 'redo',
        },
        {
          type: ToolbarElementType.SPACER,
          size: 'medium',
        },
        {
          type: ToolbarElementType.TOOL,
          config: {
            id: 'screenshot',
            factory: (viewer, persona) => new ScreenshotPersonaTool(viewer, persona),
            exclusive: true,
            icon: 'screenshot',
            name: 'screenshot',
          },
        },
      ],
    },
  ],
};
